<template>
    <div>
        <CCol sm="12">
            <CCard>
                <CCardBody class="d-flex flex-column">
                    <div class="d-flex mb-2 justify-content-end align-items-start">
                        <span class="flex-grow-1" v-if="name"><b>{{ $t('schedules.scheduleIntro') }}</b> {{ name }}</span>
                    </div>
                    <div class="d-flex flex-wrap">
                        <!-- Experience Selection -->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label class="">{{ $t('schedules.experienceMandatory') }}</label>
                            <SingleOptionDropdown 
                                :value="schedule.experienceId && schedule.experienceId > 0 ? schedule.experienceId.toString() : null" 
                                @input="val => schedule.experienceId = val ? parseInt(val) : 0" 
                                :options="experiences" 
                                :title="$t('schedules.selectExperience')" 
                                :index="1" 
                                :is-valid="errorValidator('ExperienceId')" 
                                :invalid-feedback="errorOutput['ExperienceId']">
                            </SingleOptionDropdown>
                        </div>

                        <!-- Guide Selection -->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label class="">{{ $t('schedules.guideMandatory') }}</label>
                            <SingleOptionDropdown 
                                :value="schedule.guideId && schedule.guideId > 0 ? schedule.guideId.toString() : null" 
                                @input="val => schedule.guideId = val ? parseInt(val) : 0" 
                                :options="guides" 
                                :title="$t('schedules.selectGuide')" 
                                :index="2" 
                                :is-valid="errorValidator('GuideId')" 
                                :invalid-feedback="errorOutput['GuideId']">
                            </SingleOptionDropdown>
                        </div>

                        <!-- Date -->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label class="">{{ $t('schedules.dateMandatory') }}</label>
                            <CInput 
                                type="date" 
                                :value="availableDateString" 
                                @input="val => schedule.availableDate = val"
                                :invalid-feedback="errorOutput['AvailableDate']"
                                :is-valid="errorValidator('AvailableDate')">
                            </CInput>
                        </div>

                        <!-- Start Time -->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label class="">{{ $t('schedules.startTimeMandatory') }}</label>
                            <CInput 
                                type="time" 
                                v-model="schedule.startTime" 
                                :invalid-feedback="errorOutput['StartTime']"
                                :is-valid="errorValidator('StartTime')">
                            </CInput>
                        </div>

                        <!-- Min People -->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label class="">{{ $t('schedules.minPeopleMandatory') }}</label>
                            <CInput 
                                type="number" 
                                v-model="schedule.minPeople" 
                                :invalid-feedback="errorOutput['MinPeople']"
                                :is-valid="errorValidator('MinPeople')">
                            </CInput>
                        </div>

                        <!-- Max People -->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label class="">{{ $t('schedules.maxPeopleMandatory') }}</label>
                            <CInput 
                                type="number" 
                                v-model="schedule.maxPeople" 
                                :invalid-feedback="errorOutput['MaxPeople']"
                                :is-valid="errorValidator('MaxPeople')">
                            </CInput>
                        </div>

                    </div>
                    <div role="group" class="form-group col-12 d-flex justify-content-end">
                        <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                        <CButton v-else type="button" class="btn btn-primary" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                    </div>
                    <FormLoading v-if="loading"></FormLoading>
                </CCardBody>
            </CCard>
        </CCol>
        <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup"
            v-model="showConfirmationPopup"></ConfirmationPopUp>
    </div>
</template>

<script>
import { genericValidator, errorValidator, deepClone } from "@/helpers/utils.js";
import ExperienceAvailability from "@/classes/ExperienceAvailability";
import { mapState, mapActions } from 'vuex';
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import FormLoading from '@/controls/FormLoading.vue';
import SingleOptionDropdown from '@/controls/SingleOptionDropdown.vue';
import InputGetList from "@/classes/InputGetList";

export default {
    components: { ConfirmationPopUp, FormLoading, SingleOptionDropdown },
    props: {
        baseSchedule: {
            type: Object,
            default: null,
        },
        baseScheduleName: {
            type: String,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            triedSubmit: false,
            schedule: new ExperienceAvailability(),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage: "",
        };
    },
    mounted() {
        this.loadExperiences();
        this.loadGuides();
        this.resetSchedule();
    },
    computed: {
        ...mapState({
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        ...mapState('experiences', ['getExperiencesOutput']),
        ...mapState('guides', ['getGuidesOutput']),
        ...mapState('experienceAvailabilities', ['createExperienceAvailabilityOutput']),
        ...mapState('experienceAvailabilities', ['updateExperienceAvailabilityOutput']),
        errorOutput() {
            var result = [];
            if (this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput)) {
                result = this.alertOutput.reduce((accum, currentVal) => {
                    accum[currentVal.key] = currentVal.value;
                    return accum;
                }, {});
            }
            return result;
        },
        experiences() {
            var result = [];
            if (this.getExperiencesOutput && this.getExperiencesOutput.data) {
                this.getExperiencesOutput.data.forEach(experience => {
                    result.push({ key: experience.id.toString(), label: experience.name });
                });
            }
            return result;
        },
        guides() {
            var result = [];
            if (this.getGuidesOutput && this.getGuidesOutput.data) {
                this.getGuidesOutput.data.forEach(guide => {
                    result.push({ key: guide.id.toString(), label: guide.name });
                });
            }
            return result;
        },
        availableDateString() {
            const v = this.schedule && this.schedule.availableDate ? this.schedule.availableDate : null;
            if (!v) return '';
            if (v instanceof Date) return v.toISOString().split('T')[0];
            if (typeof v === 'string') return v.split('T')[0];
            return '';
        },
        name() {
            return this.baseSchedule || this.baseScheduleName ? (this.baseSchedule ? this.baseSchedule.experience?.name : this.baseScheduleName) : null;
        }
    },
    methods: {
        ...mapActions('experiences', ['getExperiences']),
        ...mapActions('guides', ['getGuides']),
        loadExperiences() {
            var input = new InputGetList(999, 1, null, 1, "id");
            this.getExperiences(input);
        },
        loadGuides() {
            var input = new InputGetList(999, 1, null, 1, "id");
            this.getGuides(input);
        },
        resetSchedule() {
            if (!this.baseSchedule) {
                this.schedule = new ExperienceAvailability();
            } else {
                this.schedule = deepClone(new ExperienceAvailability(), this.baseSchedule);
                // Normalize time fields for HTML time inputs (expect HH:mm)
                if (this.schedule && this.schedule.startTime) {
                    const s = this.schedule.startTime.toString();
                    this.schedule.startTime = s.length >= 5 ? s.substring(0,5) : s;
                }
                // Ensure date is a yyyy-MM-dd string
                if (this.schedule && this.schedule.availableDate) {
                    const d = this.schedule.availableDate;
                    if (d instanceof Date) {
                        this.schedule.availableDate = d.toISOString().split('T')[0];
                    } else if (typeof d === 'string') {
                        this.schedule.availableDate = d.split('T')[0];
                    }
                }
            }
        },
        submitClicked() {
            this.triedSubmit = true;
            if (this.schedule.isValid()) {
                this.submitting = true;
                const payload = {
                    ...this.schedule,
                    availableDate: this.schedule.getFormattedDate(),
                    startTime: this.schedule.getFormattedStartTime()
                };
                this.$emit('submit-requested', payload);
            }
        },
        genericValidator,
        errorValidator,
        onClickConfirmationPopup(value) {
            this.showConfirmationPopup = value;
            this.$router.push({ name: "ExperienceAvailabilitiesList" });
        },
    },
    watch: {
        createExperienceAvailabilityOutput(val) {
            if (val) {
                this.confirmationMessage = this.$t('schedules.createSuccess');
                this.showConfirmationPopup = true;
            }
        },
        updateExperienceAvailabilityOutput(val) {
            if (val) {
                this.confirmationMessage = this.$t('schedules.saveSuccess');
                this.showConfirmationPopup = true;
            }
        },
        baseSchedule() {
            this.resetSchedule();
        },
        alertType() {
            this.submitting = false;
        },
        alertOutput() {
            this.submitting = false;
            if (typeof this.alertOutput === 'string' || this.alertOutput instanceof String) {
                alert(this.alertOutput);
            }
        },
        // No longer needed as we coerce via @input on the dropdowns
    }
};
</script>

<style scoped>
.showDropdown {
    display: block !important;
}

.input-group-append {
    height: fit-content;
}
</style>
